/**
 * Background API service — S3-backed (Phase 1: S3-Everything)
 * 
 * Handles fetching, uploading, and managing background images.
 * All backgrounds are stored in S3 and tracked via media_assets table.
 * 
 * API returns asset_id + presigned S3 URLs for each background.
 * The frontend stores asset_id in slide data (bgAssetId) for persistence,
 * and uses presigned URLs for immediate display.
 * 
 * AUTH CONTRACT:
 * All backend requests include Authorization: Bearer <token> from localStorage.
 * This ensures the backend can scope custom backgrounds to the authenticated user.
 */

import { getAuthHeaders } from './authHeaders';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Fetch all available backgrounds (presets + user's custom).
 * Returns: { presets: [...], custom: [...], total: N }
 * Each item has: { asset_id, filename, category, url, size }
 */
export async function fetchBackgrounds() {
  const res = await fetch(`${API}/api/backgrounds`, { headers: await getAuthHeaders() });
  if (!res.ok) throw new Error(`Failed to fetch backgrounds: ${res.status}`);
  return res.json();
}

/**
 * Upload a custom background image.
 * Image is processed server-side (crop 4:5, resize 1080x1350).
 * Returns: { asset_id, filename, category, url, size, dimensions }
 */
export async function uploadBackground(file) {
  const formData = new FormData();
  formData.append('file', file);

  // getAuthHeaders() without Content-Type — browser sets multipart boundary automatically
  const res = await fetch(`${API}/api/backgrounds/upload`, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: formData,
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Upload failed: ${res.status}`);
  }

  return res.json();
}

/**
 * Delete a custom background by asset_id.
 * Returns: { message, asset_id }
 */
export async function deleteBackground(assetIdOrFilename) {
  const res = await fetch(`${API}/api/backgrounds/${encodeURIComponent(assetIdOrFilename)}`, {
    method: 'DELETE',
    headers: await getAuthHeaders(),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || `Delete failed: ${res.status}`);
  }

  return res.json();
}

/**
 * Resolve a background asset_id to a presigned URL.
 * Returns: { asset_id, download_url, expires_in }
 */
export async function resolveBackgroundUrl(assetId) {
  const res = await fetch(`${API}/api/backgrounds/resolve/${encodeURIComponent(assetId)}`, {
    headers: await getAuthHeaders(),
  });
  if (!res.ok) return null;
  return res.json();
}

/**
 * Normalize a background URL.
 * - S3 presigned URLs are returned as-is (they include domain)
 * - Relative paths get the API base prepended
 * - null/undefined returns null
 */
export function normalizeBackgroundUrl(url) {
  if (!url) return null;
  // S3 presigned URLs are already absolute
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }
  // Legacy relative path
  return `${API}${url.startsWith('/') ? '' : '/'}${url}`;
}
